import { Injectable } from '@angular/core';
import { ProjectStatus } from '../../shared/models/project-status.enum';
import { ProjectTimelineService } from '../project-timeline.service';

@Injectable({
  providedIn: 'root',
})
export class StatusService {
  constructor(private timelineService: ProjectTimelineService) {}

  /**
   * Determina el estado actual de la tarjeta basado en la información del proyecto y los euros vendidos.
   * @param info - Información del proyecto
   * @param eurosSold - Cantidad de euros vendidos
   * @returns El estado actual como un array de strings
   */
  getCurrentStatus(info: any, eurosSold: number): string[] {
    if (!info) {
      return [ProjectStatus.UNKNOWN];
    }

    const targetAmount = info.amountToSell * (info.priceToken / 100);

    // Calcular el porcentaje vendido
    let percentageSoldNumber = 0;
    let percentageSold = '0.00';

    if (info.amountToSell && info.priceToken) {
      const result = (eurosSold * 100) / (info.amountToSell * (info.priceToken / 100));
      percentageSoldNumber = result;
      percentageSold = result.toFixed(2);
    }

    // 1. Primero verificar si está vendido
    if (info.proyectStatus === ProjectStatus.SOLD) {
      return [ProjectStatus.SOLD];
    }

    // 2. Verificar si está repartiendo dividendos
    if (info.proyectStatus === ProjectStatus.PAYING_DIVIDENDS) {
      return [ProjectStatus.PAYING_DIVIDENDS];
    }

    // 3. Verificar si está en curso (después de financiado)
    // Nota: Este método ahora es async, pero getCurrentStatus debe ser sync
    // Por ahora, verificamos solo el estado directo del proyecto
    if (percentageSoldNumber >= 100 && info.proyectStatus === ProjectStatus.IN_PROGRESS) {
      return [ProjectStatus.IN_PROGRESS];
    }

    // 4. Verificar si está financiado (solo si no está vendido ni en curso)
    if (percentageSoldNumber >= 100 && info.proyectStatus !== ProjectStatus.SOLD) {
      return [ProjectStatus.FUNDED];
    }

    // 4. Verificar si está gestionando inversiones
    if (this.isManagingInvestments(info, eurosSold)) {
      return [ProjectStatus.ACTIVE, ProjectStatus.MANAGING_REINVESTMENTS];
    }

    // 5. Verificar estados directos
    if (info.proyectStatus === ProjectStatus.UNDER_ANALYSIS) {
      return [ProjectStatus.UNDER_ANALYSIS];
    }

    // 6. Manejar estado Activo
    if (info.proyectStatus === ProjectStatus.ACTIVE) {
      if (
        !info.onlyInversors &&
        this.toFloor(info.endOfSale) !== 'Tiempo agotado' &&
        eurosSold < targetAmount
      ) {
        return [ProjectStatus.ACTIVE];
      }

      if (info.managingReinvestments ?? info.managingInvestments) {
        return [ProjectStatus.MANAGING_REINVESTMENTS];
      }

      return [ProjectStatus.ACTIVE];
    }

    return [ProjectStatus.UNKNOWN];
  }

  /**
   * Calcula el tiempo restante hasta que finalice la venta o devuelve 'Tiempo agotado'.
   * @param endOfSale - La marca de tiempo para el final de la venta
   * @returns Tiempo restante como una cadena o 'Tiempo agotado'
   */
  private toFloor(num: number): string {
    if (num === undefined || num === null || num === 0) {
      return 'No activo';
    }

    const nowTimeStamp: number = Math.floor(Date.now() / 1000);

    const secondsDiff = num - nowTimeStamp;
    const minutesDiff = secondsDiff / 60;
    const hoursDiff = minutesDiff / 60;
    const daysDiff = hoursDiff / 24;

    if (daysDiff >= 1) {
      return 'Quedan: ' + Math.floor(daysDiff) + ' días';
    }

    if (hoursDiff >= 1) {
      return 'Quedan: ' + Math.floor(hoursDiff) + ' horas';
    }

    if (minutesDiff >= 1) {
      return 'Quedan: ' + Math.floor(minutesDiff) + ' minutos';
    }

    if (secondsDiff >= 1) {
      return 'Quedan: ' + Math.floor(secondsDiff) + ' segundos';
    }

    return 'Tiempo agotado';
  }

  /**
   * Determina si el proyecto está actualmente en el estado de "Gestionando inversiones".
   * @param info - Información del proyecto
   * @param eurosSold - Cantidad de euros vendidos
   * @returns Un booleano que indica si el proyecto está gestionando inversiones
   */
  private isManagingInvestments(info: any, eurosSold: number): boolean {
    const targetAmount = info.amountToSell * (info.priceToken / 100);
    const managing = info?.managingReinvestments ?? info?.managingInvestments;
    return (
      info?.proyectStatus === ProjectStatus.ACTIVE &&
      managing &&
      eurosSold < targetAmount
    );
  }

  /**
   * Determina si un proyecto financiado debe mostrarse como "En curso".
   * Un proyecto pasa a "En curso" 2 semanas después de alcanzar 100% de financiación.
   * @param info - Información del proyecto
   * @returns Un booleano que indica si debe mostrarse como "En curso"
   */
  private async shouldShowInProgress(info: any): Promise<boolean> {
    if (!info?.id) return false;
    
    // Obtener fecha de financiación
    const fundedDate = await this.timelineService.getFundedDate(info.id);
    if (!fundedDate) return false;
    
    // Verificar si han pasado 2 semanas (14 días)
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    
    return fundedDate <= twoWeeksAgo;
  }

  /**
   * Obtiene la fecha aproximada cuando el proyecto se financió.
   * @param info - Información del proyecto
   * @returns Fecha de financiación o null si no se puede determinar
   */
  private async getFundedDate(info: any): Promise<Date | null> {
    if (!info?.id) return null;
    return await this.timelineService.getFundedDate(info.id);
  }
}